import dynamic from "next/dynamic";
import LazyLoaded from "../LazyLoaded";
import { Paperclip, Pause, Play } from "lucide-react";
import { spacing } from "@/utils/spacing";
import { FlexRowAlignCenter } from "../Flex";
import Skeleton from "@mui/material/Skeleton";
import { Spline, X } from "lucide-react";
import { themeColors } from "@/utils/themeColors";
import { useCallback, useEffect, useRef, useState } from "react";
import { LocalMessageAttachment } from "@knowt/syncing/types/common";
import CircularRectTextButton from "../CircularButton/styled/CircularRectTextButton";
import { Attachment, MediaType } from "@knowt/syncing/graphql/schema";
import { capitalize } from "@knowt/syncing/utils/stringUtils";
import ZoomableImage from "../ZoomableImage";
import CashedMessageAttachmentCard from "./components/CashedMessageAttachmentCard";
import { useS3UploadJobsSelector } from "@knowt/syncing/context/S3UploadJobContext";
import { AudioVisualizer } from "@/features/AudioRecording/AudioVisualizer";
import CircularFilledIcon from "../CircularButton/styled/CircularFilledIcon";

const WhiteboardPopup = dynamic(() => import("@/components/Tldraw/WhiteBoardPopup"), { ssr: false });

const AttachmentCard = ({
    readOnly,
    attachment,
    onWhiteBoardCardClick,
    handleRemoveAttachment,
    onImageClick,
    attachmentTagStyle,
}: {
    readOnly?: boolean;
    attachment: LocalMessageAttachment;
    onWhiteBoardCardClick: (id: string) => void;
    handleRemoveAttachment: (id: string) => void;
    onImageClick: (imageUrl: string) => void;
    attachmentTagStyle?: React.CSSProperties;
}) => {
    const playBackAudioRef = useRef<HTMLAudioElement | null>(null);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [blob, setBlob] = useState<Blob | null>(null);
    const handleRemoveAttachmentClicked = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        e.stopPropagation();
        handleRemoveAttachment(attachment.id);
    };

    const getAudioBlob = useCallback(async (audioUrl: string) => {
        const response = await fetch(audioUrl);
        if (!response.ok) {
            throw new Error("Failed to fetch the audio file");
        }
        const blob = await response.blob();
        return blob;
    }, []);

    const handlePlayAudio = useCallback(async () => {
        // biome-ignore lint/complexity/noForEach: <explanation>
        Array.from(document.querySelectorAll("audio")).forEach(audio => {
            if (audio !== playBackAudioRef.current) {
                audio.pause();
                audio.currentTime = 0;
            }
        });

        if (isAudioPlaying) {
            setIsAudioPlaying(false);
            playBackAudioRef.current?.pause();
            return;
        }
        const audioElement = playBackAudioRef.current;
        if (!audioElement) return;

        // Remove any existing ended event listeners before adding a new one
        audioElement.removeEventListener("ended", () => setIsAudioPlaying(false));
        audioElement.removeEventListener("pause", () => setIsAudioPlaying(false));

        audioElement.addEventListener("ended", () => setIsAudioPlaying(false));
        audioElement.addEventListener("pause", () => setIsAudioPlaying(false));

        if (!blob || !playBackAudioRef.current) return;

        setIsAudioPlaying(true);
        playBackAudioRef.current.src = URL.createObjectURL(blob);
        playBackAudioRef.current.play();
    }, [isAudioPlaying, blob]);

    const getAttachmentIconByType = (): JSX.Element => {
        const iconSize = 18;

        switch (true) {
            case Boolean(attachment.imageUrl):
                return <Spline size={iconSize} />;
            case attachment.type === MediaType.AUDIO:
                return isAudioPlaying ? <Pause size={iconSize} /> : <Play size={iconSize} />;
            default:
                return <Paperclip size={iconSize} />;
        }
    };

    const handleAttachmentCardClick = useCallback(() => {
        if (attachment.imageUrl) {
            // then it's a whiteboard and image url represents the whiteboard screenshot
            return onWhiteBoardCardClick(attachment.id);
        }

        if (attachment.type === MediaType.IMAGE) {
            return onImageClick(attachment.url);
        }

        if (attachment.type === MediaType.AUDIO) {
            return handlePlayAudio();
        }

        window.open(attachment.url, "_blank");
    }, [attachment, onWhiteBoardCardClick, onImageClick, handlePlayAudio]);

    const renderCardBody = () => {
        if (attachment.type === MediaType.AUDIO) {
            return blob ? (
                <AudioVisualizer
                    gap={3}
                    blob={blob}
                    width={100}
                    height={30}
                    barWidth={4}
                    barColor={themeColors.neutralBlack}
                    style={{
                        width: "100%",
                        color: themeColors.neutralBlack,
                    }}
                />
            ) : (
                <div
                    style={{ width: "10rem", height: "0.2rem", backgroundColor: themeColors.neutral2 }}
                    data-testid="auto-id-443b9ecc-679f-4cd3-8cbf-b679ee5ff7d3"
                />
            );
        }
        return attachment.imageUrl ? attachment.name : capitalize(attachment.type);
    };

    useEffect(() => {
        if (attachment.type !== MediaType.AUDIO) return;

        const fetchAudioBlob = async () => {
            const fetchedBlob = await getAudioBlob(attachment.url);
            setBlob(fetchedBlob);
        };
        fetchAudioBlob();
    }, [attachment.url, attachment.type, getAudioBlob]);

    return (
        <>
            <CircularRectTextButton
                style={{
                    gap: spacing.SM,
                    padding: `${spacing.XS} ${spacing.SM}`,
                    borderRadius: "1.2rem",
                    ...attachmentTagStyle,
                }}
                onClick={handleAttachmentCardClick}>
                <FlexRowAlignCenter style={{ gap: spacing.XS }}>
                    {getAttachmentIconByType()}
                    {renderCardBody()}
                </FlexRowAlignCenter>
                {!readOnly && (
                    <CircularFilledIcon
                        Icon={X}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            handleRemoveAttachmentClicked(e as unknown as React.MouseEvent<SVGSVGElement>);
                        }}
                        primaryColor={themeColors.neutralBlack}
                        IconProps={{ size: 18 }}
                        radius={"3rem"}
                    />
                )}
            </CircularRectTextButton>
            {attachment.type === MediaType.AUDIO && (
                <audio
                    ref={playBackAudioRef}
                    controls
                    controlsList="nodownload nofullscreen noremoteplayback noplaybackrate novolume"
                    src={attachment.url}
                    style={{ backgroundColor: "transparent", borderRadius: "14px", display: "none" }}
                    data-testid="auto-id-0ffe83a3-9012-471c-9663-ae46781a1357">
                    <track kind="captions" data-testid="auto-id-05825dd4-a9d7-486b-ba1b-1f2c713a6649" />
                </audio>
            )}
        </>
    );
};

export const RichEditorAttachmentList = ({
    className,
    messageAttachments,
    localEditorAttachments,
    handleUpdateAttachments,
    readOnly,
    attachmentTagStyle,
    style,
}: {
    className?: string;
    readOnly?: boolean;
    messageAttachments?: Attachment[] | null;
    localEditorAttachments?: LocalMessageAttachment[] | null;
    handleUpdateAttachments?: (attachments: LocalMessageAttachment[] | null) => void;
    style?: React.CSSProperties;
    attachmentTagStyle?: React.CSSProperties;
}) => {
    const [isWhiteBoardOpen, setIsWhiteBoardOpen] = useState(false);
    const [selectedWhiteBoardId, setSelectedWhiteBoardId] = useState<string | null>(null);
    const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
    const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);

    const isUploading = useS3UploadJobsSelector(s => s.isUploading);

    if ((!localEditorAttachments || !localEditorAttachments.length) && !isUploading && !messageAttachments) return null;

    const handleOnWhiteBoardCardClick = (id: string) => {
        setSelectedWhiteBoardId(id);
        setIsWhiteBoardOpen(true);
    };

    const handleRemoveAttachment = (id: string) => {
        handleUpdateAttachments?.(localEditorAttachments?.filter(a => a.id !== id) || null);
    };

    const handleReplaceWhiteboardAttachment = (whiteboardAttachment: LocalMessageAttachment) => {
        handleUpdateAttachments?.(
            localEditorAttachments?.map(a => (a.id === whiteboardAttachment.id ? whiteboardAttachment : a)) || null
        );
        setIsWhiteBoardOpen(false);
    };

    const handleOpenImageViewer = (imageUrl: string) => {
        setSelectedImageUrl(imageUrl);
        setIsImageViewerOpen(true);
    };

    return (
        <>
            <FlexRowAlignCenter
                className={className}
                style={{
                    gap: spacing.XS,
                    flexWrap: "wrap",
                    width: "100%",
                    overflowX: "hidden",
                    ...style,
                }}>
                {messageAttachments?.map(attachment => (
                    <CashedMessageAttachmentCard
                        key={attachment.url}
                        attachment={attachment}
                        attachmentTagStyle={attachmentTagStyle}
                    />
                ))}
                {localEditorAttachments?.map(attachment => (
                    <AttachmentCard
                        key={attachment.id}
                        readOnly={readOnly}
                        attachment={attachment}
                        onWhiteBoardCardClick={handleOnWhiteBoardCardClick}
                        onImageClick={handleOpenImageViewer}
                        handleRemoveAttachment={handleRemoveAttachment}
                        attachmentTagStyle={attachmentTagStyle}
                    />
                ))}
                {isUploading && (
                    <Skeleton
                        className="secondaryTextBold1"
                        variant="rectangular"
                        width={150}
                        height={35}
                        style={{
                            borderRadius: "1.2rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}>
                        Uploading file...
                    </Skeleton>
                )}
            </FlexRowAlignCenter>
            <LazyLoaded load={isWhiteBoardOpen}>
                <WhiteboardPopup
                    isOpen={isWhiteBoardOpen}
                    onClose={() => setIsWhiteBoardOpen(false)}
                    defaultWhiteboardId={selectedWhiteBoardId}
                    onWhiteboardUsageConfirm={handleReplaceWhiteboardAttachment}
                    readOnly={readOnly}
                />
            </LazyLoaded>
            <LazyLoaded load={isImageViewerOpen && !!selectedImageUrl}>
                <ZoomableImage
                    isOpen={isImageViewerOpen}
                    onClose={() => setIsImageViewerOpen(false)}
                    img={selectedImageUrl as string}
                    alt={`attachment image`}
                />
            </LazyLoaded>
        </>
    );
};

export default RichEditorAttachmentList;
